<form class="ngsild-form" [formGroup]="form">
  <div class="ngsild-form__block">
    <div class="ngsild-form__row">
      <div class="ngsild-form__field">
        <label class="ngsild-form__label" for="ngsild-title">
          Title <span class="ngsild-form__required">*</span>
        </label>
        <input
          id="ngsild-title"
          type="text"
          class="ngsild-form__input ngsild-form__input--single-line"
          formControlName="title"
          placeholder="{{ titlePlaceholder() }}"
          [class.ngsild-form__input--error]="showError('title') || isTitleUnavailable()"
        />
        @if (showError('title')) {
          <p class="ngsild-form__error">Title is required</p>
        }
        @if (hasTitleValue()) {
          <div class="ngsild-title-availability">
            @if (isCheckingTitle()) {
              <span class="ngsild-title-availability__checking">Checking availability…</span>
            } @else if (titleAvailabilityStatus() === 'available') {
              <span class="ngsild-title-availability__ok">
                <svg class="ngsild-title-availability__icon" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <circle cx="9" cy="9" r="8" fill="#4caf50" stroke="#4caf50" stroke-width="1" />
                  <path d="M6 9L8 11L12 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Title is available
              </span>
            } @else if (titleAvailabilityStatus() === 'not-available') {
              <span class="ngsild-title-availability__bad">
                <svg class="ngsild-title-availability__icon" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <circle cx="9" cy="9" r="8" fill="#d32f2f" stroke="#d32f2f" stroke-width="1" />
                  <path d="M6 6L12 12M12 6L6 12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Title is not available
              </span>
            }
          </div>
        }
      </div>

      <div class="ngsild-form__field">
        <label class="ngsild-form__label" for="ngsild-permission">
          Permission <span class="ngsild-form__required">*</span>
        </label>
        <div class="custom-dropdown-wrapper">
          <div
            id="ngsild-permission"
            class="custom-dropdown-trigger"
            [class.error]="showError('permission')"
            [class.open]="isPermissionDropdownOpen()"
            (click)="togglePermissionDropdown()"
          >
            <div class="trigger-content">
              @if (!form.controls.permission.value) {
                <span class="placeholder">Select permission</span>
              } @else {
                <span
                  class="permission-chip"
                  [class.permission-chip--public]="form.controls.permission.value === 'Open'"
                  [class.permission-chip--restricted]="form.controls.permission.value === 'Restricted'"
                  [class.permission-chip--private]="form.controls.permission.value === 'Private'"
                >
                  <img src="/public.svg" alt="" class="permission-chip__icon" aria-hidden="true" />
                  {{ getSelectedPermissionLabel() }}
                  <svg
                    class="chip-close-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    (click)="clearPermission($event)"
                  >
                    <path
                      d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              }
            </div>
            <svg
              class="dropdown-arrow"
              [class.open]="isPermissionDropdownOpen()"
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1L6 6L11 1"
                stroke="#666"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          @if (isPermissionDropdownOpen()) {
            <div class="custom-dropdown-panel" (click)="$event.stopPropagation()">
              @for (opt of permissionOptions; track opt.value) {
                <div class="dropdown-option" (click)="selectPermission(opt)">
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      class="checkbox-custom"
                      [checked]="isPermissionSelected(opt.value)"
                      readonly
                      tabindex="-1"
                    />
                  </div>
                  <span class="option-label">{{ opt.label }}</span>
                </div>
              }
            </div>
          }
        </div>
        @if (showError('permission')) {
          <p class="ngsild-form__error">Permission is required</p>
        }
      </div>
    </div>
  </div>

  <div class="ngsild-form__fields">
      <div class="ngsild-form__field">
        <label class="ngsild-form__label" for="ngsild-short-desc">
          Short Description <span class="ngsild-form__required">*</span>
        </label>
        <div class="ngsild-form__textarea-shell">
          <textarea
            id="ngsild-short-desc"
            class="ngsild-form__textarea ngsild-form__textarea--compact ngsild-form__textarea--with-count"
            formControlName="shortDescription"
            rows="3"
            maxlength="200"
            placeholder="Brief summary for the catalogue"
            [class.ngsild-form__input--error]="showError('shortDescription')"
          ></textarea>
          <span
            class="ngsild-form__textarea-count"
            [class.ngsild-form__textarea-count--warn]="shortDescriptionLength > 160"
            [class.ngsild-form__textarea-count--limit]="shortDescriptionLength >= 200"
            aria-hidden="true"
            >{{ shortDescriptionLength }}/200</span
          >
        </div>
        @if (showError('shortDescription')) {
          <p class="ngsild-form__error">
            @if (form.controls.shortDescription.errors?.['required']) {
              Short description is required
            } @else if (form.controls.shortDescription.errors?.['minlength']) {
              Short description must be at least 50 characters
            } @else if (form.controls.shortDescription.errors?.['maxlength']) {
              Short description cannot exceed 200 characters
            }
          </p>
        }
      </div>

      <div class="ngsild-form__field">
        <label class="ngsild-form__label" for="ngsild-long-desc">
          Long Description <span class="ngsild-form__required">*</span>
        </label>
        <div class="ngsild-form__textarea-shell">
          <textarea
            id="ngsild-long-desc"
            class="ngsild-form__textarea ngsild-form__textarea--tall ngsild-form__textarea--with-count"
            formControlName="longDescription"
            rows="5"
            maxlength="2000"
            placeholder="Detailed description for dataset consumers"
            [class.ngsild-form__input--error]="showError('longDescription')"
          ></textarea>
          <span
            class="ngsild-form__textarea-count"
            [class.ngsild-form__textarea-count--warn]="longDescriptionLength > 1800"
            [class.ngsild-form__textarea-count--limit]="longDescriptionLength >= 2000"
            aria-hidden="true"
            >{{ longDescriptionLength }}/2000</span
          >
        </div>
        @if (showError('longDescription')) {
          <p class="ngsild-form__error">
            @if (form.controls.longDescription.errors?.['required']) {
              Long description is required
            } @else if (form.controls.longDescription.errors?.['minlength']) {
              Long description must be at least 50 characters
            } @else if (form.controls.longDescription.errors?.['maxlength']) {
              Long description cannot exceed 2000 characters
            }
          </p>
        }
      </div>
  </div>
</form>
